import React, { useContext, useMemo } from "react";

import { IntlContext } from "context";
import ComponentList from "app/OurAd/List/ComponentList";
import AdCardSkeleton from "app/OurAd/List/AdCardSkeleton";
import { fetchOurAds } from "hooks/ad";
import { usePreviewList } from "hooks/usePreviewList";
import { AdInterface } from "types/ad";

const PREVIEW_COUNT = 6;

const SUBCATEGORY_ADS_GRID_CLASS = "grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3";

interface SubCategoryAdsContentProps {
    queryKey: string;
    fetcherParams: HashMap<unknown>;
    title: string;
    subcategory: string;
    subcategoryLabel: string;
    noResultsLabel?: string;
}

const SubCategoryAdsContent: React.FC<SubCategoryAdsContentProps> = ({
    queryKey,
    fetcherParams,
    title,
    subcategory,
    subcategoryLabel,
    noResultsLabel,
}) => {
    const intl = useContext(IntlContext);
    const { data, isLoading } = usePreviewList(fetchOurAds, fetcherParams, queryKey);

    const items = useMemo(() => {
        const list = [...(data ?? [])] as unknown as AdInterface[];
        return list.filter((ad) => ad.subcategory === subcategory);
    }, [data, subcategory]);

    const subtitle = intl.formatMessage("subcategoryPage.ads_subtitle", {
        count: items.length,
        category: subcategoryLabel,
    });

    return (
        <>
            <header className="mb-8 space-y-2">
                <h2 className="text-3xl font-black tracking-tight text-foreground sm:text-4xl">{title}</h2>
                {!isLoading && <p className="text-sm text-muted-foreground">{subtitle}</p>}
            </header>

            {isLoading ? (
                <div className={SUBCATEGORY_ADS_GRID_CLASS}>
                    {Array.from({ length: PREVIEW_COUNT }).map((_, index) => (
                        <AdCardSkeleton key={index} />
                    ))}
                </div>
            ) : items.length === 0 ? (
                <div className="text-sm text-muted-foreground" aria-live="polite">
                    {noResultsLabel}
                </div>
            ) : (
                <ComponentList items={items} gridClassName={SUBCATEGORY_ADS_GRID_CLASS} />
            )}
        </>
    );
};

export default SubCategoryAdsContent;
